import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

/**
 * Standardized Delete Alert Dialog Component
 *
 * A reusable confirmation dialog for delete actions with:
 * - Consistent styling across the app
 * - Customizable title and warning messages
 * - Primary red button with Tailwind config styling
 * - Optional bullet point format for warnings or icon-based warnings
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {string|JSX.Element} title - The title of the dialog (can be string or JSX for formatting)
 * @param {string|JSX.Element} confirmQuestion - The main confirmation question (can be string or JSX for formatting)
 * @param {string[]|JSX.Element[]} warnings - Array of warning messages to display (with or without bullets)
 * @param {boolean} showBullets - Whether to show warnings as bullet points (default: true)
 * @param {JSX.Element} warningIcon - Optional icon to display before warnings (instead of bullets)
 * @param {string} warningIconColor - Tailwind color class for the warning icon (e.g., "text-app-warning")
 * @param {string} confirmButtonText - Text for the confirm button (e.g., "Delete Case")
 * @param {JSX.Element} confirmButtonIcon - Optional icon to display before button text
 * @param {function} onConfirm - Callback when user confirms deletion
 */
const DeleteAlertDialog = ({
  open,
  onOpenChange,
  title,
  confirmQuestion,
  warnings = [],
  showBullets = true,
  warningIcon = null,
  warningIconColor = "text-app-gray-600",
  confirmButtonText = "Delete",
  confirmButtonIcon = null,
  onConfirm,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="fixed inset-0 bg-black/40 z-[200]"
          style={{
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />
        <AlertDialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[201] bg-app-white rounded-app-lg border border-app-gray-900 px-app-2xl py-app-2xl max-w-md w-full"
        >
          <div className="flex items-center gap-app-md mb-app-lg min-w-0">
            <AlertDialog.Title className="text-app-xl font-semibold text-app-gray-900 min-w-0 flex-1">
              {title}
            </AlertDialog.Title>
          </div>

          <AlertDialog.Description className="text-app-sm text-app-gray-700 mb-app-xl">
            {warnings.length > 0 && (
              showBullets ? (
                <ul className="list-disc pl-app-lg space-y-app-xs">
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              ) : warningIcon ? (
                <div className="flex gap-app-lg items-start">
                  <div className={`flex-shrink-0 mt-[2px] ${warningIconColor}`}>
                    {warningIcon}
                  </div>
                  <div className="space-y-app-xs flex-1">
                    {warnings.map((warning, index) => (
                      <div key={index}>{warning}</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-app-xs">
                  {warnings.map((warning, index) => (
                    <div key={index}>{warning}</div>
                  ))}
                </div>
              )
            )}
          </AlertDialog.Description>

          <div className="flex justify-end gap-app-md">
            <AlertDialog.Cancel asChild>
              <button className="px-app-lg py-app-sm text-app-sm font-medium text-app-gray-500 hover:text-app-gray-900 rounded-app-sm transition-colors">
                Cancel
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                onClick={handleConfirm}
                className="btn-primary bg flex items-center gap-app-xs"
              >
                {confirmButtonIcon && confirmButtonIcon}
                {confirmButtonText}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default DeleteAlertDialog;
